// Registration for the site profile's Performance tab.
//
// The shared menu builder (utils/tabItems) is not the owner of this entry:
// Performance is backed by the DQS metric rollup, which only some tenants'
// feeds populate, so it is injected by the profile alongside the tab's own
// content rather than baked into the menu every screen shares.
import type { MenuProps } from "antd";

type MenuItems = NonNullable<MenuProps["items"]>;

export const PERFORMANCE_TAB_KEY = "performance-metrics";

const SITE_OVERVIEW_GROUP_KEY = "group-site-overview";

// The "Trials" entry, after which Performance reads most naturally: the trial
// list, then how the site performed across those trials.
const ANCHOR_KEY = "performance";

/**
 * The site-profile menu with the Performance entry inserted into the Site
 * Overview group. If that group is ever renamed the entry lands at the end of
 * the menu rather than disappearing, so the tab stays reachable.
 *
 * `disabled` greys the entry out for tenants whose feed carries no metric
 * rollup at all (SITE_TAB_DISABLED), keeping it listed but unclickable.
 *
 * `first` puts Performance at the head of the group instead of after Trials
 * (PROFILE_SIMPLIFIED): where the trial list is the profile's spine, the
 * rollup reads as a summary of it and belongs after; where the site's own
 * performance is the point of the screen, it leads.
 */
export function withPerformanceTab(
  items: MenuItems,
  disabled = false,
  first = false,
): MenuItems {
  const PERFORMANCE_ITEM = {
    key: PERFORMANCE_TAB_KEY,
    label: "Performance",
    disabled,
  };

  const group = items.find(
    (item) =>
      item != null && "key" in item && item.key === SITE_OVERVIEW_GROUP_KEY,
  ) as { children?: MenuItems } | undefined;

  if (!group?.children) return [...items, PERFORMANCE_ITEM];

  const anchor = group.children.findIndex(
    (child) => child != null && "key" in child && child.key === ANCHOR_KEY,
  );
  const children = [...group.children];
  children.splice(
    first ? 0 : anchor === -1 ? children.length : anchor + 1,
    0,
    PERFORMANCE_ITEM,
  );

  return items.map((item) =>
    item === group ? { ...group, children } : item,
  ) as MenuItems;
}
